import { Box, Grid, Paper, useTheme } from "@mui/material";
import { gridClasses, GridColDef } from "@mui/x-data-grid";
import { FC, useMemo } from "react";
import { Link, Outlet } from "react-router-dom";
import { AttachmentType, Permissions } from "../../api/dtos";
import GridActions from "../../components/GridActions";
import SetLinks from "../../components/layout/SetLinks";
import { StyledButton } from "../../components/StyledButton";
import CollapsibleTablePremium from "../../components/tables/CollapsibleTablePremium";
import { createCustomToolbarProps } from "../../components/utility/CustomToolbarProps";
import AttachmentTypesContext from "../../contexts/AttachmentTypesContext";
import { DefaultProjectCreateLink } from "../../contexts/layouts/LinkContext";
import useAttachmentTypes from "../../hooks/data/useAttachmentTypes";

const useAttachmentTypeColumns = () => {

    const agentColumns = useMemo(() => {
        const columns: GridColDef<AttachmentType>[] =  [
            {
                field: "name",
                headerName: "Name",
                flex: 1
            },
            {
                field: "rank",
                headerName: "Rank",
                flex: 1
            },
            {
                field: "actions",
                headerName: '',
                renderCell: (params) => {
                    return (
                        <GridActions
                            editPermission={Permissions.AdminAccess}
                            editUrl={`/admin/attachment-types/edit/${params.row.id}`}
                        />
                    )
                },
                disableExport: true
            }
        ];

        return columns;
    }, []);

    return agentColumns;
}


const AttachmentTypes:FC = () => {

    const columns = useAttachmentTypeColumns();

    const theme = useTheme();

    const context = useAttachmentTypes();
    
    const {data: attachmentTypes, loading: attachmentTypesLoading} = context;
    
    const customToolbarProps = useMemo(() => createCustomToolbarProps({
        fileName: `Attachment Types`,
        exportTitle: `Attachment Types`,
        includeDateTime: true
    }), []);

    return (
        <AttachmentTypesContext.Provider value={context}>
            <Outlet />
            <SetLinks
                createProjectLink={DefaultProjectCreateLink}
                stageLinkEnabled={false}
                lotLinkEnabled={false}
            />
            <Grid
                container
                gap={theme.spacing(2)}
                justifyContent={"end"}
                marginBottom={theme.spacing(2)}
            >
                <StyledButton 
                        variant="contained"
                        //@ts-expect-error
                        component={Link}
                        to={`/admin/attachment-types/create`}
                        state={{
                            back: true
                        }}
                >
                    Add Attachment Type
                </StyledButton>
            </Grid>
            <Paper>
                <Box
                    sx={{
                        [`& .${gridClasses.root}`]: {
                            
                            [`& .${gridClasses.columnHeader}`]: {
                                height: 'auto !important',
                                minHeight: 56,
                                
                                [`& .${gridClasses.columnHeaderTitleContainer}`]: {
                                    whiteSpace: 'normal',
                                    
                                    [`& .${gridClasses.columnHeaderTitle}`]: {
                                        whiteSpace: 'normal',
                                        paddingTop: theme.spacing(1.5),
                                        paddingBottom: theme.spacing(1.5) 
                                    }
                                },
                                
                                [`& .${gridClasses.iconButtonContainer}`]: {
                                    width: 'unset',
                                },
                                
                                '&.column-group-header': {
                                    backgroundColor: theme.palette.grey[300],
                                    boxShadow: "1",
                                    textAlign: 'center'
                                }
                            }
                        },
                    }}
                    >
                    <CollapsibleTablePremium
                        density="compact"
                        startCollapsed={false} 
                        title={"Attachment Types"} 
                        loading={attachmentTypesLoading} 
                        columns={columns}
                        rows={attachmentTypes}
                        disableColumnMenu={false}
                        disableColumnFilter={false}
                        {...customToolbarProps}
                    />
                </Box>
            </Paper>
        </AttachmentTypesContext.Provider>
    );
}

export default AttachmentTypes;